import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { TabbedListSection } from '@app/components/TabbedListSection';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

// Sign-in codes have no customer: the platform sends them and doesn't bill
interface OTPRequestRow {
  id: string;
  created_at: string;
  customer_email: string;
  company_name: string;
  phone_number: string;
  purpose: string;
  status: string;
  verification_attempts: number;
  max_attempts: number;
  verified_at: string | null;
  provider: string;
  delivery_cost: string | null;
  ip_address: string | null;
}

const requestRepo = new AdminListRepository<OTPRequestRow>(apiClient, 'otp-requests');

function RequestsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [search, setSearch] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (statusFilter) f.status = statusFilter;
    if (purposeFilter) f.purpose = purposeFilter;
    if (search) f.search = search;
    return f;
  }, [statusFilter, purposeFilter, search]);

  const cols: DataTableColumn<OTPRequestRow>[] = [
    { id: 'created_at', header: 'When', accessor: (r) => formatDateTime(r.created_at) },
    {
      id: 'customer', header: 'Sent for',
      accessor: (r) => r.company_name
        ? <span>{r.company_name}<span className="block text-xs text-slate-500">{r.customer_email}</span></span>
        : <span className="text-slate-500">Sign-in code (platform)</span>,
    },
    { id: 'phone_number', header: 'Phone', accessor: (r) => <code className="font-mono text-xs">{r.phone_number}</code> },
    { id: 'purpose', header: 'Purpose', accessor: 'purpose' },
    {
      id: 'status', header: 'Status',
      accessor: (r) => <StatusBadge value={r.status} />,
    },
    { id: 'attempts', header: 'Tries', accessor: (r) => `${r.verification_attempts}/${r.max_attempts}` },
    {
      id: 'charge', header: 'Charge',
      accessor: (r) => (r.company_name && r.delivery_cost ? `$${r.delivery_cost}` : <span className="text-slate-400">not billed</span>),
    },
  ];

  return (
    <div>
      <div className="mb-2 flex justify-end gap-2 text-sm">
        <input
          type="search"
          placeholder="Search phone, company or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-2 py-1"
        />
        <select
          value={purposeFilter}
          onChange={(e) => setPurposeFilter(e.target.value)}
          className="rounded border px-2 py-1"
        >
          <option value="">All purposes</option>
          <option value="login">Login</option>
          <option value="registration">Registration</option>
          <option value="password_reset">Password reset</option>
          <option value="phone_verification">Phone verification</option>
          <option value="transaction">Transaction</option>
          <option value="two_factor">2FA</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded border px-2 py-1"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="verified">Verified</option>
          <option value="expired">Expired</option>
          <option value="failed">Failed</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>
      <DataTable<OTPRequestRow>
        columns={cols}
        fetcher={(p) => requestRepo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No OTP requests yet.</span>}
      />
    </div>
  );
}

export function OTPPage() {
  return (
    <TabbedListSection
      title="OTP Service"
      tabs={[
        { id: 'requests', label: 'Requests', render: () => <RequestsTab /> },
      ]}
    />
  );
}
